import { useCallback, useRef } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import { Button, CoachMark, Flex, NewBadge } from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'
import { useDefaultLandingPage } from '~publish/hooks/useDefaultLandingPage'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'

import coachmarkImage from './assets/buffer-home-coachmark.png'
import { useSetBufferHomeAsDefault } from './useSetBufferHomeAsDefault'

import styles from './BufferHomeAnnouncementCoachmark.module.css'

/**
 * Per-user dismissal id for the Buffer Home announcement coachmark. Exported so
 * other surfaces (e.g. the Preferences landing-page setting) can suppress the
 * coachmark by dismissing it.
 */
export const BUFFER_HOME_ANNOUNCEMENT_BANNER_ID =
  'buffer-home-announcement-coachmark'

/**
 * Announcement coachmark that points existing users to Buffer Home (ring +
 * popover) from the sidebar Home nav item.
 *
 * Access gating is the caller's responsibility: this only renders inside
 * `MainNavigation`'s Home nav item, which is itself gated on
 * {@link useHasAccessToBufferHome}, so it is reached only by organization
 * owners. Given that, it surfaces to users who have not yet adopted Home as
 * their landing page (`landingPage === 'default'`), and only until they
 * explicitly dismiss it — dismissal is persisted per-user via
 * {@link useDismissableBanner}.
 *
 * Renders its `children` (the Home nav item) untouched when it shouldn't show.
 */
export const BufferHomeAnnouncementCoachmark = ({
  children,
}: {
  children: ReactNode
}): JSX.Element => {
  const landingPage = useDefaultLandingPage()
  // "Got It" is an explicit dismissal, so it tracks `bannerDismissed`.
  const { isActive, dismiss } = useDismissableBanner(
    BUFFER_HOME_ANNOUNCEMENT_BANNER_ID,
    {
      trackDismiss: true,
    },
  )
  // "Set As Default" is a conversion, not a dismissal — persist the dismissal
  // for the same banner but without firing the `bannerDismissed` event.
  const { dismiss: dismissWithoutTracking } = useDismissableBanner(
    BUFFER_HOME_ANNOUNCEMENT_BANNER_ID,
  )
  const commonTrackingProperties = useCommonTrackingProperties()
  const setBufferHomeAsDefault = useSetBufferHomeAsDefault()

  // Existing users still land on Schedule (`landingPage === 'default'`); once a
  // user has set Home as their landing page they no longer need the nudge.
  const shouldShow = landingPage === 'default' && isActive

  // Track the announcement as "viewed" the first time the popover is opened
  // (on hover), not merely when the ring mounts.
  const hasTrackedView = useRef(false)
  const handleOpenChange = useCallback(
    (open: boolean): void => {
      if (open && !hasTrackedView.current) {
        hasTrackedView.current = true
        BufferTracker.bannerViewed({
          ...commonTrackingProperties,
          bannerViewed: [BUFFER_HOME_ANNOUNCEMENT_BANNER_ID],
        })
      }
    },
    [commonTrackingProperties],
  )

  const handleSetAsDefault = useCallback((): void => {
    setBufferHomeAsDefault()
    dismissWithoutTracking()
  }, [setBufferHomeAsDefault, dismissWithoutTracking])

  if (!shouldShow) {
    return <>{children}</>
  }

  return (
    <CoachMark
      onOpenChange={handleOpenChange}
      onDismiss={dismiss}
      dismissTimeout={null}
    >
      {/* Span the full nav-item width so the ring centres over the item.
          Set via `style` (not a className) so Popcorn's own `.overlay` class —
          which positions the spotlight — is preserved. */}
      <CoachMark.Overlay style={{ width: '100%' }}>
        {children}
        <CoachMark.Spotlight size="small" />
      </CoachMark.Overlay>
      <CoachMark.Content side="right" align="center" showCloseIcon={false}>
        {/* 16px (gap="md") between image, the title/paragraph group, and the
            actions. Card padding is the CoachMark default (--space-md = 16px).
            `align="stretch"` makes children full-width so the footer's buttons
            can right-align. */}
        <Flex direction="column" gap="md" align="stretch">
          <img
            src={coachmarkImage}
            alt="A preview of Buffer Home"
            className={styles.image}
          />
          {/* 8px (gap="xs") between the title and the paragraph. */}
          <Flex direction="column" gap="xs">
            {/* 8px (gap="xs") between the New tag and the title. */}
            <Flex direction="row" gap="xs" align="center">
              <NewBadge />
              <CoachMark.Title>Your week at a glance</CoachMark.Title>
            </Flex>
            <CoachMark.Description>
              Find your streak, goals, scheduled posts, unanswered comments and
              more, all in one place.
            </CoachMark.Description>
          </Flex>
          <CoachMark.Footer>
            <CoachMark.Dismiss>
              <Button variant="tertiary">Got It</Button>
            </CoachMark.Dismiss>
            <Button variant="primary" onClick={handleSetAsDefault}>
              Set as Default
            </Button>
          </CoachMark.Footer>
        </Flex>
      </CoachMark.Content>
    </CoachMark>
  )
}
